import { inject } from '@angular/core';
import {
  EduContentTOCInterface,
  EduContentTocLearningDomainFeature,
  EduContentTocQueries,
} from '@campus/dal';
import { Dictionary } from '@ngrx/entity';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store, createActionGroup, createFeature, createReducer, createSelector, on, props } from '@ngrx/store';
import { Observable, catchError, exhaustMap, filter, map, of, withLatestFrom } from 'rxjs';
import { ScopedPaletteApi } from '../api';
import { ScopedPaletteEntry, ScopedPaletteResponse } from '../models';
import {
  SCOPE_LEVELS_ORDERED,
  ScopeLevel,
  assertLoadable,
  buildScopeKey,
  isIdCovered,
  parseScopeKey,
  queryForLevel,
  scopeKeyForEntry,
} from '../utils';

/** @internal */
export type QueryStatus = 'fetched' | 'failed';

export interface ScopedPaletteState {
  entries: Record<string, ScopedPaletteEntry>;
  // Per-query outcome. `'fetched'` after `loaded`, `'failed'` after `loadFailed`.
  // `loadFailed` does not overwrite `'fetched'` — once data is in the store we
  // keep it even if a later refresh fails.
  queryStatus: Record<string, QueryStatus>;
}

/** @internal */
export const initialState: ScopedPaletteState = {
  entries: {},
  queryStatus: {},
};

export const actions = createActionGroup({
  source: 'ScopedPalette',
  events: {
    /**
     * **Resolver-side load.** "I want palette for this (level, id), with these flags."
     *
     * Dedup: skipped only when the **exact** query signature
     * `(level + id + includeChildren + includeLearningDomains + includeAncestors)`
     * has already been fetched. Two routes asking for the same id with
     * different flags will both run.
     *
     * `includeLearningDomains` and `includeAncestors` are honored at the toc
     * level only.
     */
    loadFor: props<{
      level: ScopeLevel;
      id: number;
      includeChildren?: boolean;
      includeLearningDomains?: boolean;
      includeAncestors?: boolean;
    }>(),
    /**
     * **Directive-side load.** "I need a palette for this id, at minimum."
     *
     * Dedup: no-op when **any** query variant for `(level, id)` has already
     * been attempted (fetched or failed). Used by the directive so it doesn't
     * double up when the route resolver has already loaded a richer variant.
     *
     * Internally dispatches `loadFor` with `includeChildren: false` only when
     * a fetch is needed.
     */
    ensureLoadedFor: props<{ level: ScopeLevel; id: number }>(),
    loaded: props<{ query: string; entries: ScopedPaletteEntry[] }>(),
    loadFailed: props<{ query: string; error: unknown }>(),
  },
});

/** @internal */
function mergeEntries(
  existing: Record<string, ScopedPaletteEntry>,
  incoming: ScopedPaletteEntry[]
): Record<string, ScopedPaletteEntry> {
  if (incoming.length === 0) return existing;

  const next = { ...existing };

  for (const entry of incoming) next[entry.scopeKey] = entry;

  return next;
}

export const feature = createFeature({
  name: 'ScopedPalette',
  reducer: createReducer(
    initialState,
    on(
      actions.loaded,
      (state, { query, entries }): ScopedPaletteState => ({
        ...state,
        entries: mergeEntries(state.entries, entries),
        queryStatus: { ...state.queryStatus, [query]: 'fetched' },
      })
    ),
    on(
      actions.loadFailed,
      (state, { query }): ScopedPaletteState => ({
        ...state,
        queryStatus:
          state.queryStatus[query] === 'fetched' ? state.queryStatus : { ...state.queryStatus, [query]: 'failed' },
      })
    )
  ),
  extraSelectors: ({ selectEntries, selectQueryStatus }) => ({
    selectEntryByScopeKey: (scopeKey: string) => createSelector(selectEntries, (entries) => entries[scopeKey]),
    selectIsQueryFetched: (query: string) => createSelector(selectQueryStatus, (status) => status[query] === 'fetched'),
    // True when the query resolved (fetched or failed). Used by the route resolver
    // so navigation proceeds even when the palette API call errored — a failed
    // palette must never block the page from rendering.
    selectIsQueryAttempted: (query: string) => createSelector(selectQueryStatus, (status) => status[query] != null),
    // True when we have no palette entry for (level, id) AND no query variant for
    // that id has been fetched — i.e. the directive should trigger a minimal load.
    selectNeedsLoad: (level: ScopeLevel, id: number) =>
      createSelector(
        selectEntries,
        selectQueryStatus,
        (entries, status) => !entries[buildScopeKey(level, id)] && !isIdCovered(Object.keys(status), level, id)
      ),
    // Set of toc ids that have their own toc-level palette entry in the store.
    // Lets consumers (e.g. card mappers) treat such tocs as "themed" without
    // having to inspect each entry individually.
    selectTocIdsWithPalette: createSelector(
      selectEntries,
      (entries): ReadonlySet<number> => collectIdsAtLevel(entries, 'eduContentTOC')
    ),
    // Set of learning-domain ids that have their own LD-level palette entry in
    // the store. Useful for "only theme as LD when we actually have a palette"
    // call sites — e.g. a strand title that wants its `primary-container` style
    // only when the LD's palette has been fetched (toc-detail with includeLDs).
    selectLearningDomainIdsWithPalette: createSelector(
      selectEntries,
      (entries): ReadonlySet<number> => collectIdsAtLevel(entries, 'learningDomain')
    ),
  }),
});

/** @internal */
function collectIdsAtLevel(entries: Record<string, ScopedPaletteEntry>, targetLevel: ScopeLevel): ReadonlySet<number> {
  const ids = new Set<number>();

  for (const scopeKey of Object.keys(entries)) {
    const parsed = parseScopeKey(scopeKey);
    if (parsed?.level === targetLevel) ids.add(parsed.id);
  }

  return ids;
}

// Tags each toc in the response with its depth from the DAL store. Tocs with
// no DAL entity fall back to depth 0 — the legacy single-toc cascade.
function buildTocDepthMap(
  response: ScopedPaletteResponse,
  tocEntities: Dictionary<EduContentTOCInterface>
): Map<number, number> {
  const tocDepth = new Map<number, number>();
  for (const entry of response.eduContentTOCs ?? []) {
    if (entry.eduContentTOCId == null) continue;
    tocDepth.set(entry.eduContentTOCId, tocEntities[entry.eduContentTOCId]?.depth ?? 0);
  }
  return tocDepth;
}

// Tags each LD in the response with the deepest depth at which it is linked
// to a toc that's also in this response — so a child-toc's LD outranks a
// parent-toc when the deep-cascade route flag is on. LDs with no link to any
// toc in the response fall back to depth 0.
function buildLdDepthMap(
  response: ScopedPaletteResponse,
  tocDepth: ReadonlyMap<number, number>,
  ldsByTocId: Record<number, number[]>
): Map<number, number> {
  const ldDepth = new Map<number, number>();
  for (const entry of response.learningDomains ?? []) {
    if (entry.learningDomainId == null) continue;
    let maxDepth = 0;
    for (const [tocId, depth] of tocDepth) {
      const linked = ldsByTocId[tocId] ?? [];
      if (linked.includes(entry.learningDomainId) && depth > maxDepth) maxDepth = depth;
    }
    ldDepth.set(entry.learningDomainId, maxDepth);
  }
  return ldDepth;
}

// Iterate the level table so adding a new level only requires updating
// `SCOPE_LEVELS` — this loop picks it up automatically. Toc and LD entries
// carry a `depth` that drives selector specificity downstream.
/** @internal */
export function responseToEntries(
  response: ScopedPaletteResponse,
  tocEntities: Dictionary<EduContentTOCInterface>,
  ldsByTocId: Record<number, number[]>
): ScopedPaletteEntry[] {
  const tocDepth = buildTocDepthMap(response, tocEntities);
  const ldDepth = buildLdDepthMap(response, tocDepth, ldsByTocId);

  const out: ScopedPaletteEntry[] = [];

  for (const descriptor of SCOPE_LEVELS_ORDERED) {
    const responseEntries = response[descriptor.responseField];
    if (!responseEntries?.length) continue;

    for (const entry of responseEntries) {
      const scopeKey = scopeKeyForEntry(entry);
      if (!scopeKey) continue;

      let depth: number | undefined;
      if (descriptor.level === 'eduContentTOC' && entry.eduContentTOCId != null) {
        depth = tocDepth.get(entry.eduContentTOCId);
      } else if (descriptor.level === 'learningDomain' && entry.learningDomainId != null) {
        depth = ldDepth.get(entry.learningDomainId);
      }

      out.push(depth != null ? { scopeKey, style: entry.style, depth } : { scopeKey, style: entry.style });
    }
  }

  return out;
}

/** @internal */
function apiCall(
  api: ScopedPaletteApi,
  level: ScopeLevel,
  id: number,
  includeChildren: boolean,
  includeLearningDomains: boolean,
  includeAncestors: boolean
): Observable<ScopedPaletteResponse> {
  assertLoadable(level);
  switch (level) {
    case 'method':
      return api.getForMethod(id);
    case 'eduContentBook':
      return api.getForBook({ eduContentBookId: id, includeChildren, includeLearningDomains });
    case 'eduContentTOC':
      return api.getForToc({
        eduContentTOCId: id,
        includeChildren,
        includeLearningDomains,
        includeAncestors,
      });
  }
}

/** @internal */
export const loadFor$ = createEffect(
  (actions$ = inject(Actions), api = inject(ScopedPaletteApi), store = inject(Store)) =>
    actions$.pipe(
      ofType(actions.loadFor),
      map(({ level, id, includeChildren, includeLearningDomains, includeAncestors }) => {
        const children = !!includeChildren;
        const lds = !!includeLearningDomains;
        const ancestors = !!includeAncestors;
        return { level, id, children, lds, ancestors, query: queryForLevel(level, id, children, lds, ancestors) };
      }),
      concatLatestFrom(({ query }) => store.select(feature.selectIsQueryFetched(query))),
      filter(([, alreadyFetched]) => !alreadyFetched),
      exhaustMap(([{ level, id, children, lds, ancestors, query }]) =>
        apiCall(api, level, id, children, lds, ancestors).pipe(
          withLatestFrom(
            store.select(EduContentTocQueries.getAllEntities),
            store.select(EduContentTocLearningDomainFeature.feature.selectLearningDomainIdsByTocId)
          ),
          map(
            ([response, tocEntities, ldsByTocId]): Action =>
              actions.loaded({ query, entries: responseToEntries(response, tocEntities, ldsByTocId) })
          ),
          catchError((error) => of(actions.loadFailed({ query, error })))
        )
      )
    ),
  { functional: true }
);

/** @internal */
export const ensureLoadedFor$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(actions.ensureLoadedFor),
      concatLatestFrom(({ level, id }) => store.select(feature.selectNeedsLoad(level, id))),
      filter(([, needsLoad]) => needsLoad),
      map(([{ level, id }]) => actions.loadFor({ level, id, includeChildren: false }))
    ),
  { functional: true }
);

export const effects = {
  loadFor$,
  ensureLoadedFor$,
};
